import { Injectable } from "@angular/core";
import { ProfileService } from "./profile.service";
import { ConfigService } from "./config.service";
import { LoginResult, ADPResourceService as ResourceService } from "digital-core";
import { AnalyticsEvent, CurrencyService } from "common";
import { D2CSalesActivity } from "../../../Direct2C/src/app/models";

type ResetType = "event_data" | "ecommerce_data" | "page_data" | null;

@Injectable({ providedIn: "root" })
export class GoogleAnalyticsService {
  private englishValues: any;
  private loginResult: LoginResult;
  userType = "Consumer";
  agentLevel = "";
  storeName = "";
  client = "";

  constructor(
    private profileService: ProfileService,
    private configService: ConfigService,
    private resourceService: ResourceService,
    private currencyService: CurrencyService
  ) { }

  setApplicationParameters() {
    this.client = this.resourceService.getPortalSetting("Global_GA_Client")?.Value + this.resourceService.getPortalSetting("Global_GA_Country")?.Value;
    this.loginResult = this.profileService.getUserProfile();
    if (this.loginResult) {
      this.userType = "Agent";
      this.agentLevel = this.loginResult.SystemRole;
      this.storeName = this.loginResult.BranchId;
    }
  }

  /** Helper to push data into Google Tag Manager's dataLayer */
  private pushToDataLayer(payload: any, resetType: ResetType = "event_data") {
    try {
      window["dataLayer"] = window["dataLayer"] || [];
      if (resetType) {
        window["dataLayer"].push({ [resetType]: null });
      }
      window["dataLayer"].push(payload);
    } catch (error) {
      console.error("GA Push Error:", error);
    }
  }

  // ---------- B2C Public Wrappers (for backwards compatibility) ----------
  public sessionStart() {
    try {
      let session = localStorage.getItem('ga4_session');
      let dataLayerSession = window["dataLayer"].filter((element: { event: string; }) => element.event === "session_start");
      if (session && dataLayerSession.length > 0) {
        let parseSession = JSON.parse(session);
        let creationDate: any = new Date(parseSession.startDateTime);
        let dateTimeNow: any = new Date();
        let differenceMin = Math.abs(Math.round((dateTimeNow.getTime() - creationDate.getTime()) / (1000 * 60)));
        if (differenceMin <= 30) {
          return;
        }
      }
      let sessionData = {
        event: AnalyticsEvent.SessionStart,
        session_data: {
          client: this.client,
        }
      };
      this.pushToDataLayer(sessionData, "event_data");
      localStorage.removeItem("ga4_session");
      localStorage.setItem('ga4_session', JSON.stringify({ startDateTime: new Date(), value: sessionData }));
    } catch (e) {
      console.log(e);
    }
  }

  public pageView(pagePath: string, pageTitle: string, pageLocation: string, pageReferrer: string, deviceData?: any) {
    try {
      const data = {
        event: AnalyticsEvent.PageView,
        page_data: {
          pagePath: pagePath,
          page_title: pageTitle,
          pageLocation: pageLocation,
          page_referrer: pageReferrer,
          client: this.client,
          agent_level: this.agentLevel,
          user_type: this.userType,
          storeName: this.storeName,
          item_info: deviceData,
          event: AnalyticsEvent.PageLoad
        }
      };
      this.pushToDataLayer(data, "page_data");
    } catch (e) {
      console.log(e);
    }
  }

  public appError(errorCode: number, errorType: string, errorMessage: string, pagePath: string, pageTitle: string, pageLocation: string) {
    try {
      const data = {
        event: AnalyticsEvent.ApplicationError,
        event_data: {
          page_location: pageLocation,
          page_path: pagePath,
          page_title: pageTitle,
          error_message: errorMessage,
          error_type: errorType,
          error_code: errorCode,
          hit_timestamp: Date.now(),
          user_type: this.userType
        }
      };
      this.pushToDataLayer(data, "event_data");
    } catch (e) {
      console.log(e);
    }
  }

  public ctaLink(ctaLocation: string, ctaValue: string) {
    try {
      const data = {
        event: AnalyticsEvent.CtaLink,
        event_data: {
          ctaLocation: ctaLocation,
          ctaValue: ctaValue
        }
      };
      this.pushToDataLayer(data, "event_data");
    } catch (e) {
      console.log(e);
    }
  }

  public tabClick(tabContent: string) {
    try {
      const data = {
        event: AnalyticsEvent.TabClick,
        event_data: {
          tabContent: tabContent
        }
      };
      this.pushToDataLayer(data, "event_data");
    } catch (e) {
      console.log(e);
    }
  }

  public modelView(identifier: string, modalMessage: string) {
    try {
      const data = {
        event: AnalyticsEvent.ModelView,
        event_data: {
          identifier: identifier,
          modal_message: modalMessage
        }
      };
      this.pushToDataLayer(data, "event_data");
    } catch (e) {
      console.log(e);
    }
  }

  public faqAccordion(faqQuestion: string) {
    try {
      const data = {
        event: AnalyticsEvent.FaqAccordion,
        event_data: {
          faqQuestion: faqQuestion,
          client: this.client
        }
      };
      this.pushToDataLayer(data, "event_data");
    } catch (e) {
      console.log(e);
    }
  }

  public clientApplication(currentPartner: string, aggregatorValue: string) {
    try {
      window["dataLayer"] = window["dataLayer"] || [];
      window["dataLayer"].push({ "event_data": null });
      window["dataLayer"].push({
        referrer: '',
        loggedInUser: '',
        productCategory: '',
        pageType: '',
        funnelStep: '',
        quotePromoCode: '',
        currentPartner: currentPartner,
        aggregatorValue: aggregatorValue,
      });
    } catch (e) {
      console.log(e);
    }
  }

  public addToCart(salesActivity: D2CSalesActivity) {
    try {
      const selectedCoverage = salesActivity.CoveragePlans?.find(prod => prod.IsProductSelected);
      const data = {
        event: AnalyticsEvent.AddToCart,
        event_data: {
          user_type: this.userType,
          agent_level: this.agentLevel,
          paymentPlan: null,
          planType: this.convertToEnglish(selectedCoverage.PlanType),
          storeName: this.storeName,
          ecommerce_data: {
            currencyCode: this.currencyService.currency,
            add: {
              products: this.getProductList(salesActivity, selectedCoverage.PlanType)
            }
          }
        }
      };
      this.pushToDataLayer(data, "ecommerce_data");
    } catch (e) {
      console.log(e);
    }
  }

  public checkout(stepName: string, salesActivity: D2CSalesActivity) {
    try {
      const selectedCoverage = salesActivity.CoveragePlans?.find(prod => prod.IsProductSelected);
      const selectedPayment = salesActivity.Products?.find(prod => prod.IsProductSelected);
      const deviceDetails = salesActivity.DeviceDetails;
      let data: any = {
        event: `${AnalyticsEvent.Checkout}_${stepName}`,
        event_data: {
          user_type: this.userType,
          agent_level: this.agentLevel,
          paymentPlan: selectedPayment.PaymentPlans,
          planType: this.convertToEnglish(selectedCoverage.PlanType),
          storeName: this.storeName,
          ecommerce_data: {
            currencyCode: this.currencyService.currency,
            add: {
              products: this.getProductList(salesActivity, selectedCoverage.PlanType)
            }
          }
        }
      };
      if (deviceDetails) {
        data = {
          ...data,
          event_data: {
            ...data.event_data,
            manufacturer: deviceDetails.Manufacturer,
            modelNumber: deviceDetails.ModelNumber,
            serialNumber: deviceDetails.SerialNumber,
            dateAcquistion: salesActivity.ProductSalesDate
          }
        }
      }
      this.pushToDataLayer(data, "ecommerce_data");
    } catch (e) {
      console.log(e);
    }
  }

  public transaction(salesActivity: D2CSalesActivity) {
    try {
      const selectedCoverage = salesActivity.CoveragePlans?.find(prod => prod.IsProductSelected);
      const selectedPayment = salesActivity.Products?.find(prod => prod.IsProductSelected);
      const variant = salesActivity.CoveragePlans[0];
      const totalSavings = this.getTotalSaving(salesActivity);
      const data = {
        currencyCode: this.currencyService.currency,
        event: AnalyticsEvent.Transaction,
        event_data: {
          paymentMethod: salesActivity.PaymentMethodInfo?.PaymentMethod,
          emailOptin: "",
          user_type: this.userType,
          agent_level: this.agentLevel,
          paymentPlan: selectedPayment.PaymentPlans,
          planType: this.convertToEnglish(selectedCoverage.PlanType),
          storeName: this.storeName,
          ecommerce_data: {
            purchase: {
              actionField: {
                id: salesActivity.PolicyNumber,
                affiliation: this.client,
                revenue: salesActivity.TotalValue.toFixed(2),
                tax: `${salesActivity.TotalTax}`,
                shipping: totalSavings === 0 ? "0" : `${totalSavings * -1}`,
                coupon: salesActivity.PromoCode?.Code
              }
            },
            products: this.getProductList(salesActivity, selectedCoverage.PlanType)
          }
        }
      };
      this.pushToDataLayer(data, "ecommerce_data");
    } catch (e) {
      console.log(e);
    }
  }

  private getTotalSaving(salesActivity: D2CSalesActivity) {
    let totalSavings = 0;
    salesActivity.Products.forEach(product =>
      totalSavings += product.Saving
    );
    return totalSavings;
  }

  private getProductList(salesActivity: D2CSalesActivity, variant: string) {
    let products = [];
    if (salesActivity.Products && salesActivity.Products.length > 0) {
      salesActivity.Products.forEach(product =>
        products.push(
          {
            'name': salesActivity.FullProduct, // this is the product name being covered
            'id': product.ProductCode, // SKU for the product
            'price': this.currencyService.reverseFormatNumber(product.PurchasePrice).toFixed(2), // make sure no currency code included here
            'brand': salesActivity.Make, //  brand of the product being insured
            'category': salesActivity.Model, // category of product being insured
            'variant': this.convertToEnglish(variant), // indicate if includes loss coverage or not - Yes|No
            'quantity': 1 // qty of product
          })
      );
    } else {
      products.push(
        {
          'name': salesActivity.FullProduct, // this is the product name being covered
          'id': undefined, // SKU for the product
          'price': 0, // make sure no currency code included here
          'brand': salesActivity.Make, //  brand of the product being insured
          'category': salesActivity.Model, // category of product being insured
          'variant': this.convertToEnglish(variant), // indicate if includes loss coverage or not - Yes|No
          'quantity': 1 // qty of product
        });
    }
    return products;
  }

  private convertToEnglish(value: string) {
    let values = this.resourceService.getPortalSetting('Global_Settings_OtherToEnglish').Value;
    let englishValue: string;
    if (values !== "" && !this.englishValues) {
      this.englishValues = JSON.parse(values);
      englishValue = this.englishValues[value];
    }
    let convertedValue = value;
    if (englishValue) {
      convertedValue = englishValue;
    }
    return convertedValue;
  }
}
